'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import {
  Eye, EyeOff, TrendingUp, TrendingDown, Shield, Zap,
  ArrowRight, Smartphone, Mail, Lock, User, Phone
} from 'lucide-react';

type AuthMode = 'login' | 'register' | 'phone';

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface RegisterForm {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface PhoneForm {
  phone: string;
}

const marketStats = [
  { label: 'NIFTY 50', value: '24,387.65', change: '+142.30 (+0.59%)', positive: true },
  { label: 'BANKNIFTY', value: '52,841.20', change: '+287.45 (+0.55%)', positive: true },
  { label: 'SENSEX', value: '80,234.18', change: '+398.72 (+0.50%)', positive: true },
  { label: 'Gift Nifty', value: '24,410.00', change: '+165.00 (+0.68%)', positive: true },
];

export default function AuthForm() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Phone OTP state
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpSending, setPhoneOtpSending] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState(['', '', '', '', '', '']);
  const [phoneForOtp, setPhoneForOtp] = useState('');

  // Register phone OTP state
  const [regOtpSent, setRegOtpSent] = useState(false);
  const [regOtp, setRegOtp] = useState(['', '', '', '', '', '']);

  const router = useRouter();
  const { signIn, signUp, sendPhoneOtp, verifyPhoneOtp } = useAuth();

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', rememberMe: false } });
  const registerForm = useForm<RegisterForm>({
    defaultValues: { fullName: '', email: '', phone: '', password: '', confirmPassword: '', agreeTerms: false }
  });
  const phoneForm = useForm<PhoneForm>({ defaultValues: { phone: '' } });

  // ── Email/Password Login ──
  const handleLogin = async (data: LoginForm) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Logged in successfully');
      router.push('/market-dashboard');
      router.refresh();
    } catch (error: any) {
      const msg = error?.message || 'Invalid email or password';
      loginForm.setError('email', { message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Email/Password Register ──
  const handleRegister = async (data: RegisterForm) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName });
      toast.success('Account created! Please check your email to verify your account.');
      setMode('login');
      loginForm.setValue('email', data.email);
    } catch (error: any) {
      const msg = error?.message || 'Registration failed. Please try again.';
      registerForm.setError('email', { message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Phone OTP: Send OTP (login tab) ──
  const handleSendPhoneOtp = async (data: PhoneForm) => {
    const phone = data.phone.trim();
    if (!/^[6-9]\d{9}$/.test(phone)) {
      phoneForm.setError('phone', { message: 'Enter a valid 10-digit Indian mobile number' });
      return;
    }
    setPhoneOtpSending(true);
    try {
      await sendPhoneOtp(phone);
      setPhoneForOtp(phone);
      setPhoneOtpSent(true);
      toast.success('OTP sent to +91' + phone);
    } catch (error: any) {
      const msg = error?.message || 'Failed to send OTP. Please try again.';
      phoneForm.setError('phone', { message: msg });
      toast.error(msg);
    } finally {
      setPhoneOtpSending(false);
    }
  };

  // ── Phone OTP: Verify OTP (login tab) ──
  const handleVerifyPhoneOtp = async () => {
    const token = phoneOtp.join('');
    if (token.length < 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      await verifyPhoneOtp(phoneForOtp, token);
      toast.success('Logged in successfully');
      router.push('/market-dashboard');
      router.refresh();
    } catch (error: any) {
      const msg = error?.message || 'Invalid OTP. Please try again.';
      toast.error(msg);
      setPhoneOtp(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  // ── Register: Send OTP for phone verification ──
  const handleRegSendOtp = async () => {
    const phone = registerForm.getValues('phone');
    if (!/^[6-9]\d{9}$/.test(phone)) {
      registerForm.setError('phone', { message: 'Enter a valid 10-digit Indian mobile number' });
      return;
    }
    setPhoneOtpSending(true);
    try {
      await sendPhoneOtp(phone);
      setRegOtpSent(true);
      toast.success('OTP sent to +91' + phone);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send OTP');
    } finally {
      setPhoneOtpSending(false);
    }
  };

  const handleOtpChange = (
    index: number,
    value: string,
    otpArr: string[],
    setOtp: React.Dispatch<React.SetStateAction<string[]>>,
    prefix: string
  ) => {
    if (value.length > 1) return;
    const newOtp = [...otpArr];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const next = document.getElementById(`${prefix}-${index + 1}`);
      next?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    otpArr: string[],
    setOtp: React.Dispatch<React.SetStateAction<string[]>>,
    prefix: string
  ) => {
    if (e.key === 'Backspace' && !otpArr[index] && index > 0) {
      const prev = document.getElementById(`${prefix}-${index - 1}`);
      prev?.focus();
    }
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    const map = [
      { strength: 1, label: 'Weak', color: 'bg-negative' },
      { strength: 2, label: 'Fair', color: 'bg-warning' },
      { strength: 3, label: 'Good', color: 'bg-info' },
      { strength: 4, label: 'Strong', color: 'bg-positive' },
    ];
    return map[score - 1] || { strength: 0, label: '', color: '' };
  };

  const password = registerForm.watch('password');
  const passwordStrength = getPasswordStrength(password);

  const resetPhoneOtp = () => {
    setPhoneOtpSent(false);
    setPhoneOtp(['', '', '', '', '', '']);
    setPhoneForOtp('');
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex flex-col w-[520px] xl:w-[580px] flex-shrink-0 bg-secondary border-r border-border relative overflow-hidden p-8 xl:p-12">
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#00C896" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #00C896, transparent)' }} />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #00E5FF, transparent)' }} />

        <div className="relative flex items-center gap-3 mb-10">
          <AppLogo size={40} />
          <div>
            <span className="font-bold text-xl text-foreground tracking-tight">MyTrading</span>
            <p className="text-2xs text-muted-foreground">NSE • BSE • F&O</p>
          </div>
        </div>

        <div className="relative flex-1">
          <h1 className="text-3xl xl:text-4xl font-bold text-foreground leading-tight mb-3">
            Trade Smarter<br />
            <span className="text-primary">with AI Signals</span>
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8">
            Real-time NSE/BSE prices, AI-powered market signals, full F&O capabilities, and professional-grade charting — all in one platform.
          </p>

          <div className="grid grid-cols-2 gap-3 mb-8">
            {marketStats.map((stat) => (
              <div key={`stat-${stat.label}`} className="bg-muted/50 border border-border rounded-lg p-3">
                <p className="text-2xs text-muted-foreground font-medium mb-1">{stat.label}</p>
                <p className="text-sm font-bold text-foreground font-tabular">{stat.value}</p>
                <p className={`text-2xs font-semibold font-tabular ${stat.positive ? 'text-positive' : 'text-negative'}`}>
                  {stat.positive ? <TrendingUp className="inline w-3 h-3 mr-1" /> : <TrendingDown className="inline w-3 h-3 mr-1" />}
                  {stat.change}
                </p>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {[
              { icon: Zap, label: 'AI Market Brain signals with 73% accuracy' },
              { icon: Shield, label: 'Bank-grade security with 2FA protection' },
              { icon: TrendingUp, label: 'All order types: Market, Limit, SL, Bracket, Cover' },
            ].map((f) => {
              const FeatureIcon = f.icon;
              return (
                <div key={`feat-${f.label}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FeatureIcon size={14} className="text-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground">{f.label}</p>
                </div>
              );
            })}
          </div>

          {/* Demo credentials */}
          <div className="mt-8 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-2xs font-semibold text-primary mb-1">Demo Account</p>
            <p className="text-2xs text-muted-foreground">Email: <span className="text-foreground font-mono">demo@mytrading.in</span></p>
            <p className="text-2xs text-muted-foreground">Password: <span className="text-foreground font-mono">Demo@1234</span></p>
          </div>
        </div>

        <p className="relative text-2xs text-muted-foreground mt-6">
          © 2026 MyTrading Technologies Pvt. Ltd. • SEBI Registered
        </p>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 overflow-y-auto scrollbar-thin">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-foreground">MyTrading</span>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-muted rounded-lg p-1 mb-6">
            {(['login', 'phone', 'register'] as AuthMode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => { setMode(m); resetPhoneOtp(); }}
                className={`flex-1 py-2 text-xs font-semibold rounded-md transition-all duration-200 ${
                  mode === m
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Email Login' : m === 'phone' ? 'Mobile OTP' : 'Register'}
              </button>
            ))}
          </div>

          {/* ── Email/Password Login ── */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4 fade-in">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    {...loginForm.register('email', { required: 'Email is required' })}
                    placeholder="you@example.com"
                    className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-negative">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required' })}
                    placeholder="Enter your password"
                    className="w-full bg-input border border-border rounded-md pl-9 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-negative">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...loginForm.register('rememberMe')}
                    className="w-3.5 h-3.5 rounded border-border bg-input accent-primary"
                  />
                  <span className="text-xs text-muted-foreground">Remember me</span>
                </label>
                <button type="button" className="text-xs text-primary hover:underline">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-md text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                ) : (
                  <>Sign In <ArrowRight size={14} /></>
                )}
              </button>
            </form>
          )}

          {/* ── Mobile OTP Login ── */}
          {mode === 'phone' && (
            <div className="space-y-4 fade-in">
              {!phoneOtpSent ? (
                <form onSubmit={phoneForm.handleSubmit(handleSendPhoneOtp)} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Mobile Number</label>
                    <div className="flex gap-2">
                      <div className="flex items-center px-3 bg-muted border border-border rounded-md text-sm text-muted-foreground font-semibold select-none">
                        +91
                      </div>
                      <div className="relative flex-1">
                        <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="tel"
                          {...phoneForm.register('phone', {
                            required: 'Mobile number is required',
                            pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number' }
                          })}
                          placeholder="9876543210"
                          maxLength={10}
                          className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                        />
                      </div>
                    </div>
                    {phoneForm.formState.errors.phone && (
                      <p className="mt-1 text-xs text-negative">{phoneForm.formState.errors.phone.message}</p>
                    )}
                  </div>

                  <p className="text-2xs text-muted-foreground">
                    An OTP will be sent to your registered Indian mobile number via SMS.
                  </p>

                  <button
                    type="submit"
                    disabled={phoneOtpSending}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-md text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {phoneOtpSending ? (
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    ) : (
                      <>Send OTP <Smartphone size={14} /></>
                    )}
                  </button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      OTP sent to <span className="text-foreground font-semibold">+91{phoneForOtp}</span>
                    </p>
                    <label className="block text-xs font-semibold text-foreground mb-2">Enter 6-digit OTP</label>
                    <div className="flex gap-2 justify-center">
                      {phoneOtp.map((digit, i) => (
                        <input
                          key={`phone-otp-${i}`}
                          id={`phone-otp-${i}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value, phoneOtp, setPhoneOtp, 'phone-otp')}
                          onKeyDown={(e) => handleOtpKeyDown(e, i, phoneOtp, setPhoneOtp, 'phone-otp')}
                          className="w-11 h-11 text-center bg-input border border-border rounded-md text-base font-bold text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                        />
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleVerifyPhoneOtp}
                    disabled={loading || phoneOtp.join('').length < 6}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-md text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    ) : (
                      <>Verify & Sign In <ArrowRight size={14} /></>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={resetPhoneOtp}
                    className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Change number or resend OTP
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Register Form ── */}
          {mode === 'register' && (
            <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4 fade-in">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Full Name</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    {...registerForm.register('fullName', { required: 'Full name is required' })}
                    placeholder="Your Full Name"
                    className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                  />
                </div>
                {registerForm.formState.errors.fullName && (
                  <p className="mt-1 text-xs text-negative">{registerForm.formState.errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    {...registerForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' } })}
                    placeholder="you@example.com"
                    className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                  />
                </div>
                {registerForm.formState.errors.email && (
                  <p className="mt-1 text-xs text-negative">{registerForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Mobile Number (Optional)</label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-muted border border-border rounded-md text-sm text-muted-foreground font-semibold select-none">
                    +91
                  </div>
                  <div className="relative flex-1">
                    <Smartphone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="tel"
                      {...registerForm.register('phone', { pattern: { value: /^[6-9]\d{9}$/, message: 'Invalid Indian mobile number' } })}
                      placeholder="9876543210"
                      maxLength={10}
                      className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRegSendOtp}
                    disabled={phoneOtpSending}
                    className="px-3 py-2.5 bg-primary/10 text-primary border border-primary/30 rounded-md text-xs font-semibold hover:bg-primary/20 transition-colors whitespace-nowrap disabled:opacity-60"
                  >
                    {phoneOtpSending ? '...' : regOtpSent ? 'Resend' : 'Verify'}
                  </button>
                </div>
                {registerForm.formState.errors.phone && (
                  <p className="mt-1 text-xs text-negative">{registerForm.formState.errors.phone.message}</p>
                )}
              </div>

              {regOtpSent && (
                <div className="fade-in">
                  <label className="block text-xs font-semibold text-foreground mb-1.5">Enter OTP</label>
                  <div className="flex gap-2">
                    {regOtp.map((digit, i) => (
                      <input
                        key={`reg-otp-${i}`}
                        id={`reg-otp-${i}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value, regOtp, setRegOtp, 'reg-otp')}
                        onKeyDown={(e) => handleOtpKeyDown(e, i, regOtp, setRegOtp, 'reg-otp')}
                        className="w-10 h-10 text-center bg-input border border-border rounded-md text-sm font-bold text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                      />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })}
                    placeholder="Min 8 characters"
                    className="w-full bg-input border border-border rounded-md pl-9 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {password && (
                  <div className="mt-1.5">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map((level) => (
                        <div key={`strength-${level}`} className={`h-1 flex-1 rounded-full transition-all ${level <= passwordStrength.strength ? passwordStrength.color : 'bg-muted'}`} />
                      ))}
                    </div>
                    {passwordStrength.label && <p className="text-2xs text-muted-foreground mt-0.5">{passwordStrength.label} password</p>}
                  </div>
                )}
                {registerForm.formState.errors.password && (
                  <p className="mt-1 text-xs text-negative">{registerForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...registerForm.register('confirmPassword', { required: 'Please confirm password' })}
                    placeholder="Re-enter password"
                    className="w-full bg-input border border-border rounded-md pl-9 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 transition-all"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {registerForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-xs text-negative">{registerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...registerForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                  className="w-3.5 h-3.5 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
                />
                <span className="text-xs text-muted-foreground">
                  I agree to the{' '}
                  <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
                  {' '}and{' '}
                  <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
                </span>
              </label>
              {registerForm.formState.errors.agreeTerms && (
                <p className="text-xs text-negative">{registerForm.formState.errors.agreeTerms.message}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-md text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                ) : (
                  <>Create Account <ArrowRight size={14} /></>
                )}
              </button>
            </form>
          )}

          <p className="text-center text-xs text-muted-foreground mt-4">
            {mode === 'register' ? 'Already have an account? ' : "Don't have an account? "}
            <button
              onClick={() => setMode(mode === 'register' ? 'login' : 'register')}
              className="text-primary font-semibold hover:underline"
            >
              {mode === 'register' ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}